import React, { useState, useEffect } from 'react';
import {
  Tag,
  Clock,
  Globe,
  BarChart3,
  Save,
  Chrome,
  Info,
  List,
  Loader2,
  ChevronDown,
  ChevronRight,
  Bell
} from 'lucide-react';
import { Cron } from 'react-js-cron';
import 'react-js-cron/dist/styles.css';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { TextArea } from '../common/TextArea';
import Dropdown from '../common/Dropdown';
import { WebhookSelector } from '../common/WebhookSelector';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { Schedule } from '../../context/ScheduleContext';
import { CRUDMode } from '../../context/shared/types';
import { decodeHtmlEntities } from '../../utils/htmlDecoder';
import { API_BASE_URL } from '../../config/api';  // ✅ Industry Standard: Environment-aware URLs
import TestReorderList from './TestReorderList';

import { logger, getErrorMessage } from '../../utils/logger';
/**
 * CreateScheduleModal Props
 * 
 * ✅ NEW ARCHITECTURE: Uses shared CRUD hook pattern
 * - mode: Explicit 'create' or 'edit' (no ambiguity)
 * - schedule: Fetched by context (not by modal)
 * - isLoading: Loading state handled by context
 * 
 * Industry Standard: AdminJS, Retool, Strapi pattern
 */
interface CreateScheduleModalProps {
  isOpen: boolean;
  mode: CRUDMode;              // ✅ NEW: Explicit mode ('create' or 'edit')
  schedule: Schedule | null;    // ✅ NEW: Fetched schedule data (null for create mode)
  isLoading: boolean;           // ✅ NEW: Loading state from context
  error: string | null;         // ✅ NEW: Error message from context
  onClose: () => void;
  onSaved: () => void;
}

interface CronValidation {
  isValid: boolean;
  description: string;
  nextRuns: string[];
}

const TIMEZONES = [
  { label: '🇮🇳 Asia/Kolkata (IST)', value: 'Asia/Kolkata' },
  { label: 'UTC', value: 'UTC' },
  { label: '🇺🇸 America/New_York (EST/EDT)', value: 'America/New_York' },
  { label: '🇺🇸 America/Chicago (CST/CDT)', value: 'America/Chicago' },
  { label: '🇺🇸 America/Los_Angeles (PST/PDT)', value: 'America/Los_Angeles' },
  { label: '🇬🇧 Europe/London (GMT/BST)', value: 'Europe/London' },
  { label: '🇫🇷 Europe/Paris (CET/CEST)', value: 'Europe/Paris' },
  { label: '🇯🇵 Asia/Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: '🇨🇳 Asia/Shanghai (CST)', value: 'Asia/Shanghai' },
  { label: '🇦🇺 Australia/Sydney (AEST/AEDT)', value: 'Australia/Sydney' }
];

const CreateScheduleModal: React.FC<CreateScheduleModalProps> = ({
  isOpen,
  mode,
  schedule,
  isLoading,
  error,
  onClose,
  onSaved
}) => {
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tag, setTag] = useState('');
  const [executionMode, setExecutionMode] = useState<'parallel' | 'sequential'>('parallel');
  const [browser, setBrowser] = useState('chromium');
  const [cronSchedule, setCronSchedule] = useState('0 2 * * *');
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [enabled, setEnabled] = useState(true);
  const [isRecurring, setIsRecurring] = useState(true);
  const [executionOrder, setExecutionOrder] = useState<string[]>([]);
  
  // Validation
  const [cronValidation, setCronValidation] = useState<CronValidation | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Available tags (same format as Dashboard)
  const [availableTags, setAvailableTags] = useState<{tag: string, count: number}[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  
  // Show test reorder UI
  const [showReorder, setShowReorder] = useState(false);
  
  // Webhook options state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedWebhookId, setSelectedWebhookId] = useState<string>('');
  const [disableNotifications, setDisableNotifications] = useState(false);
  const [availableWebhooks, setAvailableWebhooks] = useState<any[]>([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);

  // Load available tags
  useEffect(() => {
    const fetchTags = async () => {
      setLoadingTags(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/tags/all`);
        if (!response.ok) throw new Error('Failed to fetch tags');
        
        const data = await response.json();
        logger.debug('[SCHEDULE-MODAL] Fetched tags:', { data: data });
        // Same logic as Dashboard - extract tags with counts
        const tagsWithCounts = (data.tags || []).map((tag: string) => ({
          tag,
          count: data.tagCounts?.[tag] || 0
        }));
        logger.debug('[SCHEDULE-MODAL] Extracted tags with counts:', { data: tagsWithCounts });
        setAvailableTags(tagsWithCounts);
      } catch (error) {
        logger.error('Failed to fetch tags:', { error: getErrorMessage(error) });
      } finally {
        setLoadingTags(false);
      }
    };

    if (isOpen) {
      fetchTags();
    }
  }, [isOpen]);

  // Load available webhooks
  useEffect(() => {
    const fetchWebhooks = async () => {
      if (!isOpen) return;
      
      setLoadingWebhooks(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/webhooks`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const webhooks = data.webhooks || [];
          setAvailableWebhooks(webhooks);
          
          // Auto-select default webhook (only if not editing or not already set)
          if (mode === 'create') {
            const defaultWebhook = webhooks.find((w: any) => w.isDefault && w.isActive);
            if (defaultWebhook && !selectedWebhookId) {
              setSelectedWebhookId(defaultWebhook.id);
              logger.debug('Auto-selected default webhook:', { data: defaultWebhook.name });
            }
          }
        }
      } catch (err) {
        logger.error('Failed to fetch webhooks:', { error: getErrorMessage(err) });
      } finally {
        setLoadingWebhooks(false);
      }
    };
    
    fetchWebhooks();
  }, [isOpen, mode, selectedWebhookId]);

  // ✅ NEW: Populate form when schedule data loads (edit mode) or reset for create mode
  // Context handles fetch - modal just populates form
  useEffect(() => {
    if (!isOpen) return;  // Don't process if modal is closed

    if (schedule) {
      // ✅ Edit mode: Populate from fetched schedule
      logger.debug('[SCHEDULE-MODAL] Populating from schedule (edit mode):', { data: schedule.id });
      
      // ✅ DEFENSIVE: Decode fields even though useCRUDModal should have done it
      // This handles edge cases where schedule comes from other sources or cached data
      setName(decodeHtmlEntities(schedule.name));
      setDescription(decodeHtmlEntities(schedule.description) || '');
      setTag(decodeHtmlEntities(schedule.tag));
      setExecutionMode(schedule.executionMode);
      setBrowser(schedule.browser);
      setCronSchedule(decodeHtmlEntities(schedule.cronSchedule));
      setTimezone(decodeHtmlEntities(schedule.timezone));  // ✅ CRITICAL: Decode timezone!
      setEnabled(schedule.enabled);
      setIsRecurring(schedule.isRecurring !== false);
      setExecutionOrder(schedule.executionOrder || []);
      setShowReorder(false);
      
      // Load webhook preferences
      setSelectedWebhookId(schedule.webhookId || '');
      setDisableNotifications(schedule.disableNotifications || false);
    } else {
      // ✅ Create mode: Reset to defaults
      logger.debug('[SCHEDULE-MODAL] Resetting form (create mode)');
      setName('');
      setDescription('');
      setTag('');
      setExecutionMode('parallel');
      setBrowser('chromium');
      setCronSchedule('0 2 * * *');
      setTimezone('Asia/Kolkata');
      setEnabled(true);
      setIsRecurring(true);
      setExecutionOrder([]);
      setShowReorder(false);
      
      // Reset webhook preferences (default will be auto-selected by webhook fetch effect)
      setSelectedWebhookId('');
      setDisableNotifications(false);
    }
    
    setErrors({});
    setCronValidation(null);
  }, [schedule, isOpen]);

  // Validate cron when it changes
  useEffect(() => {
    if (!cronSchedule) return;

    const validateCron = async () => {
      setValidating(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/schedules/validate-cron`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            cronExpression: cronSchedule, 
            timezone: timezone || 'Asia/Kolkata'  // Ensure never null/undefined
          })
        });

        const data = await response.json();
        if (data.success) {
          setCronValidation(data.data);
          setErrors(prev => ({ ...prev, cronSchedule: '' }));
        } else {
          setCronValidation(null);
          setErrors(prev => ({ ...prev, cronSchedule: data.error }));
        }
      } catch (error) {
        setCronValidation(null);
      } finally {
        setValidating(false);
      }
    };

    const timer = setTimeout(validateCron, 500);
    return () => clearTimeout(timer);
  }, [cronSchedule, timezone]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // ✅ FIXED: Safe validation - handle undefined/null values
    if (!name || !name.trim()) newErrors.name = 'Name is required';
    if (!tag || !tag.trim()) newErrors.tag = 'Tag is required';
    if (!cronSchedule || !cronSchedule.trim()) newErrors.cronSchedule = 'Schedule is required';
    if (!cronValidation?.isValid) newErrors.cronSchedule = 'Invalid cron expression';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      showError('Please fix the errors before saving');
      return;
    }

    setLoading(true);
    try {
      // ✅ FIXED: Safe payload construction - handle undefined/null values
      const payload = {
        name: (name || '').trim(),
        description: (description || '').trim() || undefined,
        tag: (tag || '').trim(),
        executionMode,
        browser,
        cronSchedule: (cronSchedule || '').trim(),
        timezone,
        enabled,
        isRecurring: isRecurring,
        executionOrder: executionMode === 'sequential' && executionOrder.length > 0 ? executionOrder : null,
        webhookId: selectedWebhookId || null,
        disableNotifications: disableNotifications,
        createdBy: mode === 'create' ? user?.id : undefined  // ✅ Add createdBy from authenticated user
      };

      // ✅ NEW: Use mode prop instead of checking schedule object
      const url = mode === 'edit'
        ? `${API_BASE_URL}/api/schedules/${schedule!.id}`
        : `${API_BASE_URL}/api/schedules`;
      
      const method = mode === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save schedule');
      }

      showSuccess(`Schedule ${mode === 'edit' ? 'updated' : 'created'} successfully`);
      onSaved();
    } catch (error: any) {
      showError(`Failed to save schedule: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Show loading spinner while fetching schedule in edit mode
  if (isLoading) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Loading Schedule..."
        size="xl"
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">
            Loading schedule data...
          </span>
        </div>
      </Modal>
    );
  }

  // ✅ Show error if fetch failed
  if (error) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Error"
        size="md"
      >
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? 'Edit Schedule' : 'Create Schedule'}
      size="xl"
    >
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="space-y-4">
          <Input
            label="Schedule Name"
            placeholder="e.g., Nightly Smoke Tests"
            value={name}
            onChange={setName}
            error={errors.name}
            required
          />

          <TextArea
            label="Description"
            placeholder="Optional description of what this schedule does..."
            value={description}
            onChange={setDescription}
            rows={2}
          />
        </div>

        {/* Test Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Test Configuration
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Test Tag <span className="text-red-500">*</span>
            </label>
            <Dropdown
              options={[
                { value: '', label: loadingTags ? 'Loading tags...' : availableTags.length === 0 ? 'No tags available' : 'Select a tag...' },
                ...availableTags.map(({tag, count}) => ({ 
                  value: tag, 
                  label: tag,
                  badge: `${count} ${count === 1 ? 'test' : 'tests'}`
                }))
              ]}
              value={tag}
              onChange={(value) => setTag(value as string)}
              icon={<Tag className="h-4 w-4 text-indigo-600" />}
              disabled={loadingTags || availableTags.length === 0}
            />
            {errors.tag && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.tag}</p>
            )}
            {availableTags.length === 0 && !loadingTags && (
              <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                💡 No tags found. Create tests with tags (e.g., @smoke, @regression) first.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Execution Mode <span className="text-red-500">*</span>
            </label>
            <div className="flex border-2 border-gray-300 dark:border-slate-600 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setExecutionMode('parallel')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  executionMode === 'parallel'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600'
                }`}
              >
                <BarChart3 className="w-4 h-4 inline mr-2" />
                Parallel (Fast)
              </button>
              <button
                type="button"
                onClick={() => setExecutionMode('sequential')}
                className={`flex-1 px-4 py-3 text-sm font-medium border-l-2 border-gray-300 dark:border-slate-600 transition-colors ${
                  executionMode === 'sequential'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600'
                }`}
              >
                <List className="w-4 h-4 inline mr-2" />
                Sequential (Ordered)
              </button>
            </div>
          </div>

          {executionMode === 'sequential' && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                    Sequential mode runs tests one at a time. You can customize the execution order.
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowReorder(true)}
                    disabled={!tag}
                    icon={<List className="w-4 h-4" />}
                  >
                    {executionOrder.length > 0 ? `Custom Order (${executionOrder.length} tests)` : 'Set Execution Order'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <Dropdown
            label="Browser"
            options={[
              { value: 'chromium', label: 'Chromium', icon: <Chrome className="h-4 w-4" /> },
              { value: 'firefox', label: 'Firefox' },
              { value: 'webkit', label: 'WebKit (Safari)' }
            ]}
            value={browser}
            onChange={(value) => setBrowser(value as string)}
            icon={<Chrome className="h-4 w-4 text-indigo-600" />}
          />
        </div>

        {/* Notification Settings */}
        <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
          >
            <div className="flex items-center gap-2">
              {showAdvanced ? (
                <ChevronDown className="w-4 h-4 text-gray-500 dark:text-slate-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500 dark:text-slate-400" />
              )}
              <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                Notification Settings
              </span>
            </div>
            <Bell className="w-4 h-4 text-gray-400" />
          </button>
          
          {showAdvanced && (
            <div className="p-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/30">
              <WebhookSelector
                webhooks={availableWebhooks}
                selectedWebhookId={selectedWebhookId}
                onWebhookChange={setSelectedWebhookId}
                disableNotifications={disableNotifications}
                onDisableChange={(checked) => {
                  setDisableNotifications(checked);
                  if (checked) {
                    logger.debug('Notifications disabled for schedule:', { data: name });
                  }
                }}
                loading={loadingWebhooks}
              />
            </div>
          )}
        </div>

        {/* Schedule Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Schedule Configuration
          </h3>

          {/* Visual Cron Builder - Industry Standard */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
              Schedule *
            </label>
            
            {/* Quick Presets */}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCronSchedule('*/15 * * * *')}
                className="px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors border border-blue-200 dark:border-blue-800"
              >
                Every 15 min
              </button>
              <button
                type="button"
                onClick={() => setCronSchedule('0 * * * *')}
                className="px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors border border-blue-200 dark:border-blue-800"
              >
                Hourly
              </button>
              <button
                type="button"
                onClick={() => setCronSchedule('0 2 * * *')}
                className="px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors border border-blue-200 dark:border-blue-800"
              >
                Daily 2 AM
              </button>
              <button
                type="button"
                onClick={() => setCronSchedule('0 9 * * 1-5')}
                className="px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors border border-blue-200 dark:border-blue-800"
              >
                Weekdays 9 AM
              </button>
            </div>

            {/* Visual Cron Builder */}
            <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
              <Cron
                value={cronSchedule}
                setValue={setCronSchedule}
                clearButton={false}
                allowEmpty="never"
                className="react-js-cron-field"
              />
            </div>
            
            {/* Validation Status */}
            {validating && (
              <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg animate-pulse">
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  Validating schedule...
                </p>
              </div>
            )}
            
            {/* Success Preview */}
            {cronValidation?.isValid && !validating && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-800 dark:text-green-300">
                      ✓ {cronValidation.description}
                    </p>
                    {cronValidation.nextRuns?.[0] && (
                      <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                        Next run: {new Date(cronValidation.nextRuns[0]).toLocaleString('en-IN', { timeZone: timezone })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Error State */}
            {errors.cronSchedule && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-900 dark:text-red-300">
                  {errors.cronSchedule}
                </p>
              </div>
            )}
          </div>

          <Dropdown
            label="Timezone"
            options={TIMEZONES}
            value={timezone}
            onChange={(value) => setTimezone(value as string)}
            icon={<Globe className="h-4 w-4 text-indigo-600" />}
          />

          <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-slate-100">
                Enable schedule immediately
              </div>
              <div className="text-xs text-gray-600 dark:text-slate-400">
                If unchecked, schedule will be created but not active
              </div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors border-2 border-indigo-200 dark:border-indigo-800">
            <input
              type="checkbox"
              checked={isRecurring}
              onChange={(e) => setIsRecurring(e.target.checked)}
              className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-slate-100 flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                Recurring schedule
              </div>
              <div className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                {isRecurring 
                  ? '✅ Schedule will run repeatedly based on cron expression'
                  : '⚠️ Schedule will run once and then be automatically disabled'
                }
              </div>
            </div>
          </label>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            loading={loading}
            disabled={!cronValidation?.isValid}
            icon={<Save className="w-4 h-4" />}
            className="!bg-indigo-600 dark:!bg-indigo-700 hover:!bg-indigo-700 dark:hover:!bg-indigo-600"
          >
            {schedule ? 'Update Schedule' : 'Create Schedule'}
          </Button>
        </div>
      </div>

      {/* Test Reorder Modal */}
      {showReorder && (
        <TestReorderList
          isOpen={showReorder}
          onClose={() => setShowReorder(false)}
          tag={tag}
          initialOrder={executionOrder}
          onSave={(order) => {
            setExecutionOrder(order);
            setShowReorder(false);
          }}
        />
      )}
    </Modal>
  );
};

export default CreateScheduleModal;

